import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {PaginatedResponseV2} from "../../../types/PaginatedResponse";
import {SearchInput} from "./SearchInput";
import {useDebounce} from "use-debounce";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {anoop, noop} from "../../../lib/misc/noop";

type HasID = { id: string };


type PaginatedSelectorParams<T> = {
    headers: string[],
    getColumns: (item: T) => (string|string[])[],
    selectedIDs?: string[],
    setSelectedIDs?: (selectedIDs: string[]) => void,
    getItems: (search: string, page: number) => Promise<PaginatedResponseV2<T> | null>
    disableSelect?: boolean
    hideSearch?: boolean
    hideCheckboxes?: boolean
    onAdd?: (selectedID: string) => Promise<void>
    onRemove?: (selectedID: string) => Promise<void>
}

function diff(a: string[], b: string[]): string[] {
    const exclude = new Set(b);
    return a.filter(item => !exclude.has(item));
}


export function PaginatedSelector<T extends HasID>(
    {
        headers,
        getItems,
        getColumns,
        selectedIDs = [],
        setSelectedIDs = noop,
        disableSelect = false,
        hideSearch = false,
        hideCheckboxes = false,
        onAdd = anoop,
        onRemove = anoop,
    }: PaginatedSelectorParams<T>
) {

    const searchInputRef = useRef<HTMLInputElement>(null);

    const [totalPages, setTotalPages] = useState(0);
    const [items, setItems] = useState<T[]>([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState<string>("");
    const [page, setPage] = useState(1);

    const [debouncedSearch] = useDebounce(search, 300)


    const searchKey = useMemo(() => `${page}-${debouncedSearch}`, [debouncedSearch, page]);
    const prevSearchKey = useRef("")

    const [itemsLoading, setItemsLoading] = useState<Record<string, boolean>>({})

    const runSearch = useCallback((search: string, page: number) => {
        setLoading(true)
        getItems(search, page).then((result) => {
            if (!result) return
            setItems(result.results)
            setTotalPages(result.totalPages ?? 0)
        }).finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        const newSearchKey = `${page}-${debouncedSearch}`
        if (newSearchKey == prevSearchKey.current) return
        prevSearchKey.current = newSearchKey
        runSearch(debouncedSearch, page)
    }, [debouncedSearch, page, searchKey]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch])

    useEffect(() => {
        console.log("F")
        if(!searchInputRef.current) return
        searchInputRef.current.focus()
        console.log("F2")
    }, []);


    const rows = useMemo(() => {
        return items.map(i => getColumns(i))
    }, [items])

    const gridClass = useMemo(() => {
        return `grid grid-cols-${headers.length} flex-1`;
    }, [headers])


    const handleOnAdd = useCallback(async (id: string) => {
        setItemsLoading({...itemsLoading, selected: true})

        try {
            await onAdd(id)
        } catch(e) {

        }
        setItemsLoading({...itemsLoading, selected: false})

    }, [itemsLoading, onAdd])


    const handleOnRemove = useCallback(async (id: string) => {
        setItemsLoading({...itemsLoading, selected: true})

        try {
            await onRemove(id)
        } catch(e) {

        }
        setItemsLoading({...itemsLoading, selected: false})

    }, [itemsLoading, onAdd])


    const handleSelect = useCallback(async (id: string, selected: boolean) => {
        setItemsLoading({...itemsLoading, selected: true})


        try {
            if (selected) {
                setSelectedIDs([...selectedIDs, id]);
                await onAdd(id)
            } else {
                setSelectedIDs(selectedIDs.filter(i => i !== id));
                await onRemove(id)
            }
        } catch (e) {
            console.error(e)

            if (selected) {
                setSelectedIDs(selectedIDs.filter(i => i !== id));
            } else {
                setSelectedIDs([...selectedIDs, id]);
            }
        }

        setItemsLoading({...itemsLoading, selected: false})


    }, [itemsLoading])


    const allSelected = useMemo(() => {
        const res = items.filter(i => selectedIDs?.includes(i.id))
        return res.length == items.length

    }, [items, selectedIDs])

    const handleSelectAll = useCallback(async () => {


        setLoading(true)

        const ids = items.map(i => i.id)

        if (allSelected) {
            const newIDs = diff(selectedIDs, ids)
            setSelectedIDs(newIDs)
            ids.forEach(id => handleOnRemove(id))
        } else {
            const newIDs = [...selectedIDs, ...ids]
            console.log(newIDs)
            setSelectedIDs(Array.from(new Set(newIDs)))
            ids.forEach(id => handleOnAdd(id))
        }

        setLoading(false)


    }, [search, items, allSelected])


    const selected = useCallback((id: string) => {
        return selectedIDs.includes(id);
    }, [selectedIDs]);


    const inputClassName = useMemo(() => {
        if(!loading) return "scale-150"
        return "scale-150 opacity-50"
    }, [loading])

    return (
        <div className="flex flex-col gap-4">
            {!hideSearch && (<SearchInput ref={searchInputRef} initialValue={search} onChange={setSearch}/>)}
            <div>
                <div className="flex gap-4 p-3">
                    {!disableSelect && !hideCheckboxes && (<div className="w-[30px]">
                        <input type="checkbox"
                               className={inputClassName}
                               onChange={handleSelectAll}
                               checked={allSelected && !loading}
                        />

                    </div>)}


                    <div className={gridClass}>
                        {/* <div>*/}
                        {/*    <input type="checkbox" className="scale-150"/>*/}
                        {/* </div>*/}
                        {headers.map((header, i) => (
                            <div className="font-bold px-2" key={i}>{header}</div>
                        ))}
                    </div>
                </div>
                <hr className={"border-light-light-grey"}/>

                {loading && (
                    <div className="flex justify-center my-4">
                        <LoadingSpinner/>
                    </div>
                )}


                <div>
                    {!loading && rows.map((row, i) => (
                        <Row
                            key={items[i].id}
                            id={items[i].id}
                            selected={selected(items[i].id)}
                            handleSelect={handleSelect}
                            row={row}
                            disableSelect={disableSelect}
                            hideCheckboxes={hideCheckboxes}
                            loading={itemsLoading[items[i].id] ?? false}
                        />
                    ))}
                    {!loading && rows.length == 0 && (
                        <div className={"text-center mt-8 mb-8"}>No results found</div>
                    )}
                </div>
                <div>
                    <Paginator currentPage={page} totalPages={totalPages} onChange={setPage}/>
                </div>
            </div>
        </div>

    )

}

type RowProps = {
    id: string,
    row: (string|string[])[],
    handleSelect?: (id: string, selected: boolean) => void,
    selected: boolean,
    disableSelect?: boolean,
    hideCheckboxes?: boolean,
    loading?: boolean
}

function Row(
    {
        id,
        row,
        handleSelect = noop,
        selected,
        disableSelect,
        hideCheckboxes,
        loading
    }: RowProps,
) {

    const onSelect = useCallback(() => {
        if (disableSelect) {
            return
        }
        handleSelect(id, !selected);
    }, [selected, handleSelect, id]);

    const gridClass = useMemo(() => {
        return `grid grid-cols-${row.length} flex-1`;
    }, [row])


    return (
        <div
            className={`flex gap-4 border-b last:border-none border-light-light-grey p-3 ${!disableSelect && "cursor-pointer"} ${selected && !disableSelect ? "bg-white-blue" : ""}`}
            onClick={onSelect}>
            {!disableSelect && !hideCheckboxes && (<div className="w-[30px]">
                <input type="checkbox"
                       className="scale-150"
                       checked={selected}
                       disabled={loading}
                />

            </div>)}
            <div className={`${gridClass}`}>
                {row.map((item, i) => (
                    <div key={i}
                         className="px-2 truncate overflow-ellipsis">
                        {typeof item === "string" ? (item) : (
                            <div>
                                {item.map(i => (
                                    <div key={i}>{i}</div>
                                ))}
                            </div>

                        )}

                    </div>
                ))}
            </div>
        </div>

    )
}


type PaginatorProps = {
    currentPage: number;
    totalPages: number;
    onChange: (page: number) => void;
}

const Paginator: React.FC<PaginatorProps> = ({currentPage, totalPages, onChange}) => {
    const handleFirst = useCallback(() => {
        if (currentPage !== 1) {
            onChange(1);
        }
    }, [currentPage, onChange]);

    const handlePrev = useCallback(() => {
        if (currentPage > 1) {
            onChange(currentPage - 1);
        }
    }, [currentPage, onChange]);

    const handleNext = useCallback(() => {
        if (currentPage < totalPages) {
            onChange(currentPage + 1);
        }
    }, [currentPage, totalPages, onChange]);

    const handleLast = useCallback(() => {
        if (currentPage !== totalPages) {
            onChange(totalPages);
        }
    }, [currentPage, totalPages, onChange]);

    return (
        <div className="flex items-center justify-center gap-2 py-4">
            <button
                onClick={handleFirst}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                First
            </button>
            <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Prev
            </button>
            <span className="px-3 py-1 text-sm font-medium text-gray-700">
        Page {currentPage} of {totalPages}
      </span>
            <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Next
            </button>
            <button
                onClick={handleLast}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Last
            </button>
        </div>
    );
};

